import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Typography, Space, message, Descriptions, Tag } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import moment from 'moment';

const { Title } = Typography;

const CollectionResults = () => {
    const [task, setTask] = useState(null);
    const [responses, setResponses] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        fetchResults();
    }, [id]);

    const fetchResults = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/data-collection/tasks/${id}/responses`);
            if (response.data.success) {
                setTask(response.data.data.task);
                setResponses(response.data.data.responses);
            } else {
                message.error('获取结果失败: ' + response.data.message);
            }
        } catch (error) {
            console.error('获取结果出错:', error);
            message.error('获取结果出错');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!task || !responses.length) {
            message.warning('没有可导出的数据');
            return;
        }

        // 构建表头
        const headers = ['提交时间', '班级规范名', '学生姓名', '提交端'];
        task.fields.forEach(field => {
            headers.push(field.label);
        });

        // 构建数据行
        const exportData = responses.map(res => {
            const row = [
                moment(res.submitted_at).format('YYYY-MM-DD HH:mm:ss'),
                res.class_name,
                res.student_name,
                res.submitter_role === 'student' ? '教室桌面' : (res.submitter_role === 'parent' ? '家长' : '教师')
            ];

            // 解析每个动态字段的答案
            task.fields.forEach(field => {
                let val = res.response_data[field.id];
                if (Array.isArray(val)) {
                    val = val.join(', ');
                }
                row.push(val !== undefined && val !== null ? val : '');
            });

            return row;
        });

        exportData.unshift(headers);

        // 生成Excel
        const ws = XLSX.utils.aoa_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "收集结果");

        // 下载
        const fileName = `${task.title}-统计结果-${moment().format('YYYYMMDD')}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    // 动态生成表格的列
    const getColumns = () => {
        if (!task) return [];

        const baseColumns = [
            {
                title: '学生',
                dataIndex: 'student_name',
                key: 'student_name',
                fixed: 'left',
                width: 100
            },
            {
                title: '班级',
                dataIndex: 'class_name',
                key: 'class_name',
                width: 120
            },
            {
                title: '提交端',
                dataIndex: 'submitter_role',
                key: 'submitter_role',
                width: 100,
                render: (role) => {
                    if (role === 'student') return <Tag color="blue">教室终端</Tag>;
                    if (role === 'parent') return <Tag color="green">微信家长</Tag>;
                    return <Tag>{role}</Tag>;
                }
            }
        ];

        const dynamicColumns = task.fields.map(field => ({
            title: field.label,
            key: `field_${field.id}`,
            dataIndex: ['response_data', field.id],
            width: 150,
            render: (val) => Array.isArray(val) ? val.join(', ') : val
        }));

        const endColumns = [
            {
                title: '提交时间',
                dataIndex: 'submitted_at',
                key: 'submitted_at',
                width: 160,
                render: (text) => moment(text).format('MM-DD HH:mm:ss')
            }
        ];

        return [...baseColumns, ...dynamicColumns, ...endColumns];
    };

    return (
        <div style={{ paddingBottom: 24 }}>
            <Card bordered={false} style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Space align="center" style={{ marginBottom: 16 }}>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/data-collection')} type="text" />
                        <Title level={4} style={{ margin: 0 }}>收集结果详情</Title>
                    </Space>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchResults}>刷新</Button>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} disabled={responses.length === 0}>
                            导出Excel
                        </Button>
                    </Space>
                </div>

                {task && (
                    <Descriptions bordered size="small" column={{ xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }}>
                        <Descriptions.Item label="表单名称">{task.title}</Descriptions.Item>
                        <Descriptions.Item label="当前状态">
                            {task.status === 'active' ? <Tag color="processing">收集中</Tag> : <Tag>已结束</Tag>}
                        </Descriptions.Item>
                        <Descriptions.Item label="收集目标">
                            {task.target === 'desktop' ? '教室桌面端' : (task.target === 'wechat_parent' ? '微信小程序' : '全部')}
                        </Descriptions.Item>
                        <Descriptions.Item label="已提交份数">
                            <span style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>{responses.length}</span> 份
                        </Descriptions.Item>
                        <Descriptions.Item label="截止时间" span={2}>
                            {task.deadline ? moment(task.deadline).format('YYYY-MM-DD HH:mm') : '无限制'}
                        </Descriptions.Item>
                        <Descriptions.Item label="描述" span={3}>
                            {task.description || '无'}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Card>

            <Card bordered={false} title="详细响应数据">
                <Table
                    columns={getColumns()}
                    dataSource={responses}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    pagination={{ defaultPageSize: 20 }}
                    bordered
                />
            </Card>
        </div>
    );
};

export default CollectionResults;
